import { useCallback, useEffect, useMemo, useState } from "react";

import { AnalyticsDataSource, type AttributionFilter, Metadata } from "@doitintl/cmp-models";
import { Box, Link, Stack, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import assign from "lodash/assign";

import { AlertsTxt } from "../../../assets/texts/CloudAnalytics";
import DataHubCheckbox from "../../../Components/DataHubCheckbox/DataHubCheckbox";
import FilterDialog from "../../../Components/Dialogs/CloudAnalytics/FilterDialog";
import { getMode } from "../../../Components/Dialogs/CloudAnalytics/utils";
import { useAnalyticsDimensions } from "../../../Components/hooks/cloudAnalytics/useAnalyticsDimensions/useAnalyticsDimensions";
import { useCloudAnalyticsMetadata } from "../../../Components/hooks/cloudAnalytics/useCloudAnalyticsMetadata";
import { DimensionSelector } from "../../../Components/Selects/CloudAnalytics/DimensionSelector";
import { useCustomerContext } from "../../../Context/CustomerContext";
import { type MetadataOption } from "../../../types";
import { getFilteredDimension, getFilteredDimensionFromDimensions } from "../alerts/shared";
import { FilterModeOption } from "../allocations/types";
import AttributionBuilderChips from "../attributions/attributionBuilder/AttributionBuilderChips";
import { useAttributionsWithEKSDataCheck, useCreateAttributionHandler } from "../attributions/hooks";
import { FixedFilters } from "../utilities";

type Props = {
  dataSource: AnalyticsDataSource;
  defaultFilters?: AttributionFilter[];
  showCreateNewAttributionOnDropDown?: boolean;
  onFiltersChanged: (filters: AttributionFilter[]) => void;
  isCurrentUserEditor: boolean;
  onDataSourceChanged: (dataSource: AnalyticsDataSource) => void;
};

export const DimensionsDropdown = ({
  dataSource,
  defaultFilters,
  showCreateNewAttributionOnDropDown,
  onFiltersChanged,
  isCurrentUserEditor,
  onDataSourceChanged,
}: Props) => {
  const { customer } = useCustomerContext();
  const [filterDialogOpen, setFilterDialogOpen] = useState<boolean>(false);
  const [filters, setFilters] = useState<AttributionFilter[]>(defaultFilters ?? []);
  const [dimension, setDimension] = useState<MetadataOption | null>(null);
  const { metadata: metadataSnapshots, hasDataHub } = useCloudAnalyticsMetadata();
  const { attributions } = useAttributionsWithEKSDataCheck();

  const { dimensions, getLabelsDimensions } = useAnalyticsDimensions({
    metadataSnapshots,
    attributions,
    defaultDataSource: dataSource,
  });

  const filteredDimensions = useMemo(() => dimensions?.filter((d) => d.data.type !== Metadata.DATETIME), [dimensions]);

  const cloudProviders = useMemo(() => {
    const clouds = dimensions?.find((d) => d.id === `${Metadata.FIXED}:${FixedFilters.CLOUD}`);
    return clouds?._values.datahub ?? [];
  }, [dimensions]);

  const handleNewAttribution = useCreateAttributionHandler({
    baseUrl: `/customers/${customer.id}/analytics/attributions`,
    mixpanelEventName: "analytics.attributions.new",
    newTab: true,
  });

  const onSave = useCallback(
    (filterType: FilterModeOption, filtersOrRegex: string[] | string, inverse: boolean) => {
      if (!dimension?.id) {
        return;
      }
      const isRegexp =
        filterType === FilterModeOption.MATCHES_REGEX || filterType === FilterModeOption.DOES_NOT_MATCHES_REGEX;

      let regexp: string | null = null;
      let values: string[] | null = null;
      if (isRegexp && typeof filtersOrRegex === "string") {
        regexp = filtersOrRegex;
      } else if (Array.isArray(filtersOrRegex) && !isRegexp) {
        values = [...filtersOrRegex];
      }

      const filter: AttributionFilter = {
        id: dimension.id,
        values,
        regexp,
        inverse,
        allowNull: false,
        field: dimension.data.field,
        key: dimension.data.key,
        type: dimension.data.type,
        mode: getMode(filterType),
      };

      if (dimension.data.nullFallback) {
        const nullIndex = filter.values?.findIndex((v) => v === dimension.data.nullFallback);
        if (nullIndex !== undefined && nullIndex !== -1) {
          filter.allowNull = true;
          filter.values?.splice(nullIndex, 1);
        }
      }
      setDimension(getFilteredDimension(dimension, filter));
      setFilters([filter]);
      onFiltersChanged([filter]);
      setFilterDialogOpen(false);
    },
    [dimension, onFiltersChanged]
  );

  const handleCancelFilter = useCallback(
    (_: MetadataOption[]) => {
      setFilterDialogOpen(false);
      if (!filters.length) {
        setDimension(null);
      }
    },
    [filters.length, setDimension, setFilterDialogOpen]
  );

  const handleSelectOption = useCallback(
    async (option: MetadataOption | null, filter?: AttributionFilter) => {
      if (!filter) {
        // when the user selects a new dimension, we reset the filters
        setDimension(option);
        setFilters([]);
        onFiltersChanged([]);
      }
      if (!option) {
        return;
      }

      const { newLabels } = await getLabelsDimensions([{ key: option.data.key, type: option.data.type }]);
      const updatedOption = assign(option, newLabels[0]);

      setDimension(updatedOption);
      setFilterDialogOpen(true);
    },
    [getLabelsDimensions, setDimension, setFilterDialogOpen, setFilters, onFiltersChanged]
  );

  const onEditDimension = useCallback(() => {
    if (filters.length === 0) {
      return;
    }
    setFilterDialogOpen(true);
  }, [filters.length, setFilterDialogOpen]);

  useEffect(() => {
    if (!filterDialogOpen) {
      setDimension(getFilteredDimensionFromDimensions(dimensions ?? [], defaultFilters?.[0]));
    }
  }, [defaultFilters, dimensions, setDimension, filterDialogOpen]);

  return (
    <Box>
      <Grid container>
        <Grid size={12}>
          <DimensionSelector
            onDimensionSelected={handleSelectOption}
            selectedDimension={dimension}
            dimensions={filteredDimensions ?? []}
            disabled={!isCurrentUserEditor}
            textFieldProps={{
              label: AlertsTxt.DIMENSION,
              required: true,
            }}
            onEdit={onEditDimension}
            showCreateNewAttributionOnDropDown={showCreateNewAttributionOnDropDown}
            onNewAttributionClicked={handleNewAttribution}
            required={true}
          />
        </Grid>
        <Grid
          sx={{
            mt: 1,
          }}
          size={12}
        >
          <DataHubCheckbox
            checked={dataSource === AnalyticsDataSource.BILLING_DATAHUB}
            cloudProviders={cloudProviders}
            customerId={customer.id}
            dataSource={dataSource}
            disabled={!isCurrentUserEditor || !hasDataHub}
            onChange={(_, checked) => {
              onDataSourceChanged(checked ? AnalyticsDataSource.BILLING_DATAHUB : AnalyticsDataSource.BILLING);
            }}
          />
        </Grid>
        {filters.length > 0 && (
          <Box
            sx={{
              mt: 3,
              width: "100%",
            }}
          >
            {filters.map((f) => (
              <AttributionBuilderChips
                key={f.id}
                filter={f}
                nullFallback={dimension?.data.nullFallback ?? null}
                lightStyle={true}
                shouldWrap={true}
              />
            ))}
          </Box>
        )}
        {!showCreateNewAttributionOnDropDown && (
          <Stack
            direction="row"
            spacing={0.5}
            sx={{
              color: "text.secondary",
              mt: 3,
            }}
          >
            <Typography variant="body1" color="inherit">
              {AlertsTxt.NEED_ADVANCED_LOGIC}{" "}
            </Typography>
            <Link color="inherit" variant="body1" underline="always" href="#" onClick={handleNewAttribution}>
              {AlertsTxt.CREATE_NEW_ALLOCATION}
            </Link>
          </Stack>
        )}
      </Grid>
      {dimension && filterDialogOpen && (
        <FilterDialog
          disableEscapeKeyDown={false}
          onCancel={handleCancelFilter}
          isOptionDisabled={() => false}
          onClose={() => {
            setFilterDialogOpen(false);
          }}
          onSave={onSave}
          filter={filters[0]}
          open={filterDialogOpen}
          selected={dimension}
        />
      )}
    </Box>
  );
};
